import { useStartExportAssetBatches } from "./useExportAssetBatches";
import { useExportExecutionArns } from "./useExportExecutionArns";
import { useAdEngineActions } from "redux/assetExporter/assetExporter.slice";
import { StringParam, useQueryParams } from "use-query-params";
import { useFeedContext } from "screens/assetExporter/feedConfigurationV2/shared/contexts/FeedContext";
import {
  RecommendedColumnMatches,
  ExportDestination,
  AdInfoUpdates,
  AdType,
  PortalOptionMatches,
} from "screens/assetExporter/feedConfigurationV2/exportDrawer/types";
import { useAppSelector } from "../useAppSelector";
import { AppState } from "redux/store";
import { getExportAssetBatchesBody } from "screens/assetExporter/feedConfigurationV2/exportDrawer/utils/getExportAssetBatchesBody";

interface UseAssetBatchExportGeneration {
  feedId: string;
  selectedRows: string[];
  selectedAssetBatches: string[];
  exportDestination: ExportDestination;
  columnMatches?: RecommendedColumnMatches;
  adInfoUpdates?: AdInfoUpdates;
  adType?: AdType | null;
  portalOptionMatches?: PortalOptionMatches;
}

export const useAssetBatchExportGeneration = ({
  feedId,
  selectedRows,
  selectedAssetBatches,
  exportDestination,
  columnMatches,
  adInfoUpdates,
  adType,
  portalOptionMatches,
}: UseAssetBatchExportGeneration) => {
  const [queryParams] = useQueryParams({ feedName: StringParam });
  const { mutate: exportAssetBatches, isLoading: isStartingExport } =
    useStartExportAssetBatches();
  const { assetBatchesForSelectedRows } = useFeedContext();
  const user = useAppSelector((state: AppState) => state.auth.user);
  const { exportExecutionArns, handleSetExportExecutionArns } =
    useExportExecutionArns();
  const { setExportFeedName, setExportAssetBatchNames, setExportRowCount } =
    useAdEngineActions();

  const exportAssetBatchNames = assetBatchesForSelectedRows
    .filter(assetBatch =>
      selectedAssetBatches.includes(assetBatch.assetBatchId ?? ""),
    )
    .map(assetBatch => assetBatch.name);

  const handleStartExportAssetBatches = ({
    onSettled,
  }: {
    onSettled?: () => void;
  }) => {
    if (!exportDestination) return;
    const body = getExportAssetBatchesBody({
      feedId,
      user,
      selectedRows,
      selectedAssetBatches,
      exportDestination,
      columnMatches,
      adInfoUpdates,
      adType,
      portalOptionMatches,
    });

    exportAssetBatches(body, {
      onSuccess: data => {
        if (!data) return;
        handleSetExportExecutionArns(
          exportExecutionArns.concat(data.executionArn),
        );
        if (exportDestination === ExportDestination.AdLibrary) {
          setExportFeedName({ exportFeedName: queryParams.feedName });
          setExportAssetBatchNames({ exportAssetBatchNames });
          setExportRowCount({ exportRowCount: selectedRows.length });
        }
      },
      onSettled: () => onSettled?.(),
    });
  };

  return {
    isStartingExport,
    handleStartExportAssetBatches,
  };
};
